import { type ReactNode } from 'react';

import Drawer from '@/components/wrapped/Drawer';
import FactList, {
  type FactGroup,
} from '@/karehero-app/tell-us-once/intake/FactList';

/**
 * Somebody's record, editable, a row at a time.
 *
 * The recipient and every carer open into this: they are the same object seen
 * from different rows of the same card, so one drawer serves both and the two
 * cannot drift apart.
 *
 * Saving is the difference from the member's own review, which settles a row
 * as you leave it. A care expert is editing somebody else's record, and a
 * phone number changing because a click landed somewhere else is not a thing
 * they should have to notice. So a row is opened, changed and saved, and
 * leaving it alone leaves it alone.
 *
 * The Save is in the row rather than in the footer. A footer button collects
 * changes the drawer then has to hold, mark and discard on close, which is a
 * second state to keep straight and one more way to lose an edit. A row that
 * saves itself has no pending anything.
 */
const FactDrawer = ({
  open,
  title,
  subtitle,
  groups,
  collapsible,
  footer,
  expandable,
  onSave,
  onClose,
}: {
  open: boolean;
  title: string;
  subtitle?: string;
  groups: FactGroup[];
  /**
   * Sections fold shut. For a record long enough that its section names are
   * the way around it, which a form is and a person is not.
   */
  collapsible?: boolean;
  /**
   * A pinned action under the rows.
   *
   * Not a Save: see above. A form has somewhere for the answers to go once
   * they are in, and the people drawers do not, which is why this is optional
   * rather than something the drawer draws for itself.
   */
  footer?: ReactNode;
  /**
   * Offers the header link that takes the panel to the full frame. For a form,
   * where 227 questions at 440px wide is a very long column; not for a person,
   * whose facts fit.
   */
  expandable?: boolean;
  /**
   * A row was saved, named and with its new answer. The caller confirms it:
   * the drawer stays open.
   *
   * The people drawers ignore both arguments and only toast. A form has to
   * write the answer back, which is why the row says which one it was.
   */
  onSave: (id: string, value: string) => void;
  onClose: () => void;
}) => (
  <Drawer
    open={open}
    title={title}
    subtitle={subtitle}
    footer={footer}
    expandable={expandable}
    onClose={onClose}
  >
    {/* Edited in place. The member app opens a row in a drawer, which is its
        phone pattern and has no business inside a desktop drawer that is
        already the thing a row would open into. */}
    <FactList
      groups={groups}
      collapsible={collapsible}
      edit='inline'
      save='explicit'
      onChange={onSave}
    />
  </Drawer>
);

export default FactDrawer;
